import React, { useEffect, useState } from 'react';
import {
  View, Text, FlatList, TouchableOpacity, Modal, TextInput,
  StyleSheet, ActivityIndicator, Alert, ScrollView, Switch,
  useWindowDimensions, Image,
} from 'react-native';
import {
  fetchSellers, searchSellers, registerSeller, updateSeller,
  deleteSeller, uploadSellerImage, deleteStorageUrl,
} from '../query/sellerService';
import { SellerModel } from '../model/sellerModel';

const C = {
  primary: '#21808D', success: '#10b981', successLight: '#d1fae5',
  danger: '#ef4444', bg: '#0E1512', textPrimary: '#1a2332',
  textMuted: '#64748b', border: '#e2e8f0',
};

const STEPS = [
  { title: 'Account Setup',      sub: 'Login credentials for this seller',  icon: '🔑' },
  { title: 'Personal Info',      sub: 'Name and contact details',           icon: '👤' },
  { title: 'Address',            sub: 'Physical and map address',           icon: '🏠' },
  { title: 'Business Documents', sub: 'GST, PAN and registration numbers',  icon: '📋' },
  { title: 'Location & Branch',  sub: 'Coordinates and branch assignment',  icon: '📍' },
  { title: 'Photo & Status',     sub: 'Profile photo and account status',   icon: '📷' },
];

type SellerDoc = SellerModel & { docId: string };

const emptyForm = () => ({
  name: '', phone: '', email: '', password: '',
  address: '', mapAddress: '', branchId: '',
  gstNumber: '', panNumber: '', tinNumber: '', tradeNumber: '',
  lattitude: '', longitude: '',
  active: true, image: '',
});

// Defined outside to prevent TextInput focus loss on re-render
const F = ({ label, value, onChange, keyType, multiline, readonly, secure }: any) => (
  <View style={wi.fg}>
    <Text style={wi.label}>{label}</Text>
    <TextInput
      style={[wi.input, multiline && { height: 80 }, readonly && { backgroundColor: '#f1f5f9', color: C.textMuted }]}
      value={value} onChangeText={readonly ? undefined : onChange}
      placeholder={label} keyboardType={keyType || 'default'}
      multiline={multiline} placeholderTextColor={C.textMuted}
      editable={!readonly} secureTextEntry={secure} autoCapitalize="none"
    />
  </View>
);

export default function SellersScreen() {
  const { width } = useWindowDimensions();
  const isMobile = width < 600;

  const [sellers, setSellers] = useState<SellerDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [searchTerm, setSearchTerm] = useState('');
  const [step, setStep] = useState(0);

  const load = async () => {
    setLoading(true);
    try { setSellers(await fetchSellers(null)); }
    catch { Alert.alert('Error', 'Failed to load sellers'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onSearch = async (text: string) => {
    setSearchTerm(text);
    if (!text.trim()) { load(); return; }
    setLoading(true);
    try { setSellers(await searchSellers(text.trim())); }
    catch { Alert.alert('Error', 'Search failed'); }
    setLoading(false);
  };

  const setF = (key: string, val: any) => setForm(prev => ({ ...prev, [key]: val }));

  const openCreate = () => { setForm(emptyForm()); setEditingDocId(null); setStep(0); setModalVisible(true); };

  const openEdit = (seller: SellerDoc) => {
    setForm({
      name: seller.name ?? '', phone: seller.phone ?? '',
      email: seller.email ?? '', password: '',
      address: seller.address ?? '', mapAddress: seller.mapAddress ?? '',
      branchId: seller.branchId ?? '',
      gstNumber: seller.gstNumber ?? '', panNumber: seller.panNumber ?? '',
      tinNumber: seller.tinNumber ?? '', tradeNumber: seller.tradeNumber ?? '',
      lattitude: String(seller.lattitude ?? ''), longitude: String(seller.longitude ?? ''),
      active: seller.active ?? true, image: (seller as any).image ?? '',
    });
    setEditingDocId(seller.docId);
    setStep(0); setModalVisible(true);
  };

  const validateStep = (): boolean => {
    if (step === 0) {
      if (!editingDocId && !form.email.trim()) { Alert.alert('Required', 'Email is required'); return false; }
      if (!editingDocId && form.password.length < 6) { Alert.alert('Required', 'Password must be at least 6 characters'); return false; }
    }
    if (step === 1) {
      if (!form.name.trim()) { Alert.alert('Required', 'Full name is required'); return false; }
    }
    return true;
  };

  const goNext = () => { if (validateStep()) setStep(s => Math.min(s + 1, STEPS.length - 1)); };
  const goBack = () => setStep(s => Math.max(s - 1, 0));

  const handleImagePick = () => {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = 'image/*';
    input.onchange = async (e: any) => {
      const file: File = e.target.files?.[0];
      if (!file) return;
      setImgUploading(true);
      try {
        const id = editingDocId ?? `seller_${Date.now()}`;
        setF('image', await uploadSellerImage(file, id));
      } catch { Alert.alert('Error', 'Image upload failed'); }
      finally { setImgUploading(false); }
    };
    input.click();
  };

  const handleSave = async () => {
    if (!validateStep()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name, phone: form.phone,
        address: form.address, mapAddress: form.mapAddress,
        branchId: form.branchId, gstNumber: form.gstNumber,
        panNumber: form.panNumber, tinNumber: form.tinNumber,
        tradeNumber: form.tradeNumber,
        lattitude: parseFloat(form.lattitude) || 0,
        longitude: parseFloat(form.longitude) || 0,
        active: form.active, image: form.image,
      };
      if (editingDocId) {
        await updateSeller(editingDocId, payload);
        setSellers(prev => prev.map(s => s.docId === editingDocId ? { ...s, ...payload } : s));
        Alert.alert('Success', 'Seller updated');
      } else {
        await registerSeller(form.email, form.password, payload as any);
        Alert.alert('Success', 'Seller registered successfully');
        load();
      }
      setModalVisible(false);
    } catch (e: any) {
      Alert.alert('Error', e?.message ?? 'Failed to save seller');
    }
    setSaving(false);
  };

  const onDelete = (seller: SellerDoc) => {
    Alert.alert('Delete Seller', `Delete "${seller.name}"?`, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: async () => {
        try {
          await deleteSeller(seller.docId, (seller as any).image);
          setSellers(prev => prev.filter(s => s.docId !== seller.docId));
        } catch { Alert.alert('Error', 'Failed to delete seller'); }
      }},
    ]);
  };


  // ── Step content ──────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 0:
        return editingDocId ? (
          <>
            <F label="Email (read-only)" value={form.email} readonly />
            <View style={wi.infoBox}>
              <Text style={wi.infoTxt}>To change the email, re-register the seller.</Text>
            </View>
          </>
        ) : (
          <>
            <F label="Email *" value={form.email} onChange={(v: string) => setF('email', v)} keyType="email-address" />
            <F label="Password * (min 6 chars)" value={form.password} onChange={(v: string) => setF('password', v)} secure />
          </>
        );
      case 1:
        return (
          <>
            <F label="Full Name *" value={form.name} onChange={(v: string) => setF('name', v)} />
            <F label="Phone" value={form.phone} onChange={(v: string) => setF('phone', v)} keyType="phone-pad" />
          </>
        );
      case 2:
        return (
          <>
            <F label="Address" value={form.address} onChange={(v: string) => setF('address', v)} multiline />
            <F label="Map Address" value={form.mapAddress} onChange={(v: string) => setF('mapAddress', v)} />
          </>
        );
      case 3:
        return (
          <>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="GST Number" value={form.gstNumber} onChange={(v: string) => setF('gstNumber', v)} /></View>
              <View style={{ flex: 1 }}><F label="PAN Number" value={form.panNumber} onChange={(v: string) => setF('panNumber', v)} /></View>
            </View>
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="TIN Number" value={form.tinNumber} onChange={(v: string) => setF('tinNumber', v)} /></View>
              <View style={{ flex: 1 }}><F label="Trade Number" value={form.tradeNumber} onChange={(v: string) => setF('tradeNumber', v)} /></View>
            </View>
          </>
        );
      case 4:
        return (
          <>
            <F label="Branch ID" value={form.branchId} onChange={(v: string) => setF('branchId', v)} />
            <View style={wi.row}>
              <View style={{ flex: 1 }}><F label="Latitude" value={form.lattitude} onChange={(v: string) => setF('lattitude', v)} keyType="decimal-pad" /></View>
              <View style={{ flex: 1 }}><F label="Longitude" value={form.longitude} onChange={(v: string) => setF('longitude', v)} keyType="decimal-pad" /></View>
            </View>
          </>
        );
      case 5:
        return (
          <>
            {/* Photo */}
            <Text style={wi.sectionLabel}>Profile Photo</Text>
            <View style={wi.photoSection}>
              <TouchableOpacity style={wi.photoWrap} onPress={handleImagePick} disabled={imgUploading}>
                {imgUploading ? <ActivityIndicator color={C.primary} /> :
                  form.image ? <Image source={{ uri: form.image }} style={wi.photoImg} resizeMode="cover" /> :
                  <View style={wi.photoPlaceholder}>
                    <Text style={{ fontSize: 32 }}>🏪</Text>
                    <Text style={wi.photoHint}>Tap to upload</Text>
                  </View>
                }
              </TouchableOpacity>
              <View style={{ gap: 8 }}>
                <TouchableOpacity style={wi.uploadBtn} onPress={handleImagePick} disabled={imgUploading}>
                  <Text style={wi.uploadBtnTxt}>{form.image ? 'Change Photo' : 'Upload Photo'}</Text>
                </TouchableOpacity>
                {form.image ? (
                  <TouchableOpacity style={wi.removeBtn} onPress={async () => { await deleteStorageUrl(form.image); setF('image', ''); }}>
                    <Text style={wi.removeBtnTxt}>Remove</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>

            {/* Status */}
            <Text style={[wi.sectionLabel, { marginTop: 16 }]}>Account Status</Text>
            <View style={wi.switchRow}>
              <View>
                <Text style={wi.switchLabel}>Account Active</Text>
                <Text style={wi.switchSub}>Allow seller access to the platform</Text>
              </View>
              <Switch value={form.active} onValueChange={v => setF('active', v)} trackColor={{ false: C.border, true: C.primary }} thumbColor="#fff" />
            </View>
          </>
        );
      default: return null;
    }
  };

  // ── Card ──────────────────────────────────────────────────────────
  const getInitials = (name?: string) => {
    if (!name) return 'S';
    const p = name.split(' ');
    return (p.length > 1 ? p[0][0] + p[1][0] : name[0]).toUpperCase();
  };

  const renderSeller = ({ item }: { item: SellerDoc }) => {
    const img = (item as any).image;
    return (
      <View style={cs.card}>
        <View style={cs.cardHeader}>
          {img
            ? <Image source={{ uri: img }} style={cs.avatarImg} />
            : <View style={[cs.avatar, { backgroundColor: item.active ? C.primary : '#94a3b8' }]}>
                <Text style={cs.avatarTxt}>{getInitials(item.name)}</Text>
              </View>
          }
          <View style={{ flex: 1 }}>
            <Text style={cs.name} numberOfLines={1}>{item.name || 'Unknown Seller'}</Text>
            <Text style={cs.email} numberOfLines={1}>{item.email || '—'}</Text>
            {item.phone ? <Text style={cs.phone}>{item.phone}</Text> : null}
          </View>
          <View style={[cs.badge, { backgroundColor: item.active ? C.successLight : '#f1f5f9' }]}>
            <Text style={[cs.badgeTxt, { color: item.active ? C.success : C.textMuted }]}>
              {item.active ? 'Active' : 'Inactive'}
            </Text>
          </View>
        </View>
        <View style={cs.infoGrid}>
          {item.address ? <View style={cs.infoRow}><Text style={cs.infoIcon}>📍</Text><Text style={cs.infoTxt} numberOfLines={1}>{item.address}</Text></View> : null}
          {item.gstNumber ? <View style={cs.infoRow}><Text style={cs.infoIcon}>🏷️</Text><Text style={cs.infoTxt}>GST: {item.gstNumber}</Text></View> : null}
          {item.branchId ? <View style={cs.infoRow}><Text style={cs.infoIcon}>🏢</Text><Text style={cs.infoTxt}>Branch: {item.branchId}</Text></View> : null}
        </View>
        <View style={cs.actions}>
          <TouchableOpacity style={cs.editBtn} onPress={() => openEdit(item)}><Text style={cs.editTxt}>Edit</Text></TouchableOpacity>
          <TouchableOpacity style={cs.delBtn} onPress={() => onDelete(item)}><Text style={cs.delTxt}>Delete</Text></TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View style={cs.container}>
      <View style={cs.header}>
        <span className="material-icons-round" style={{ fontSize: 20, color: '#4ECDC4', lineHeight: 1, flexShrink: 0 }}>store</span>
        <View style={{ flex: 1 }}>
          <Text style={cs.pageTitle}>Sellers</Text>
          <Text style={cs.pageSub}>{sellers.length} registered sellers</Text>
        </View>
        <TouchableOpacity style={cs.addBtn} onPress={openCreate}>
          <Text style={cs.addBtnTxt}>+ New Seller</Text>
        </TouchableOpacity>
      </View>

      <View style={cs.searchBox}>
        <Text style={{ fontSize: 14, marginRight: 8 }}>🔍</Text>
        <TextInput style={cs.searchInput} placeholder="Search by name..." value={searchTerm} onChangeText={onSearch} placeholderTextColor={C.textMuted} />
        {searchTerm ? <TouchableOpacity onPress={() => onSearch('')}><Text style={{ color: C.textMuted, padding: 4 }}>✕</Text></TouchableOpacity> : null}
      </View>

      {loading ? (
        <View style={cs.loader}><ActivityIndicator size="large" color={C.primary} /></View>
      ) : (
        <FlatList
          data={sellers} keyExtractor={item => item.docId} renderItem={renderSeller}
          numColumns={isMobile ? 1 : 2} key={isMobile ? 'one' : 'two'}
          columnWrapperStyle={!isMobile ? { gap: 12 } : undefined}
          contentContainerStyle={{ paddingBottom: 40 }}
          ListEmptyComponent={
            <View style={cs.empty}>
              <Text style={{ fontSize: 48 }}>🏪</Text>
              <Text style={cs.emptyTitle}>No sellers found</Text>
            </View>
          }
        />
      )}

      {/* ── Step Wizard Modal ── */}
      <Modal visible={modalVisible} animationType="slide" transparent={false}>
        <View style={wi.screen}>
          <View style={wi.topBar}>
            <TouchableOpacity style={wi.closeBtn} onPress={() => setModalVisible(false)}>
              <Text style={wi.closeTxt}>✕</Text>
            </TouchableOpacity>
            <Text style={wi.topTitle}>{editingDocId ? 'Edit Seller' : 'Register Seller'}</Text>
            <View style={{ width: 36 }} />
          </View>

          <View style={wi.progressArea}>
            <View style={wi.progressBarOuter}>
              <View style={[wi.progressBarInner, { width: `${((step + 1) / STEPS.length) * 100}%` as any }]} />
            </View>
            <View style={wi.progressRow}>
              <Text style={wi.progressLabel}>{STEPS[step].icon}  {STEPS[step].title}</Text>
              <Text style={wi.progressCount}>{step + 1} / {STEPS.length}</Text>
            </View>
            <Text style={wi.progressSub}>{STEPS[step].sub}</Text>
            <View style={wi.dots}>
              {STEPS.map((_, i) => (
                <TouchableOpacity key={i} onPress={() => setStep(i)} style={[wi.dot, i === step && wi.dotActive, i < step && wi.dotDone]}>
                  {i < step
                    ? <Text style={wi.dotTxtDone}>✓</Text>
                    : <Text style={[wi.dotTxt, i === step && { color: '#fff' }]}>{i + 1}</Text>
                  }
                </TouchableOpacity>
              ))}
            </View>
          </View>

          <ScrollView style={{ flex: 1 }} contentContainerStyle={wi.scrollContent} showsVerticalScrollIndicator={false} keyboardShouldPersistTaps="handled">
            {renderStep()}
          </ScrollView>

          <View style={wi.footer}>
            {step > 0
              ? <TouchableOpacity style={wi.backBtn} onPress={goBack}><Text style={wi.backBtnTxt}>← Back</Text></TouchableOpacity>
              : <View style={{ flex: 1 }} />
            }
            {step < STEPS.length - 1 ? (
              <TouchableOpacity style={wi.nextBtn} onPress={goNext}>
                <Text style={wi.nextBtnTxt}>Next →</Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity style={[wi.saveBtn, saving && { opacity: 0.6 }]} onPress={handleSave} disabled={saving}>
                {saving ? <ActivityIndicator color="#fff" size="small" /> :
                  <Text style={wi.saveBtnTxt}>{editingDocId ? 'Update Seller' : 'Register Seller'}</Text>}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
}

// ── Card styles ───────────────────────────────────────────────────
const cs = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0E1512', padding: 16 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#0A1712', marginTop: -16, marginHorizontal: -16, marginBottom: 16, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)' },
  pageTitle: { fontSize: 15, fontWeight: '800', color: '#fff' },
  pageSub: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  addBtn: { backgroundColor: '#21808D', paddingHorizontal: 20, paddingVertical: 10, borderRadius: 10 },
  addBtnTxt: { color: '#fff', fontWeight: '600', fontSize: 14 },

  searchBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#17201B', borderRadius: 12, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', paddingHorizontal: 14, marginBottom: 16 },
  searchInput: { flex: 1, height: 46, fontSize: 14, color: '#E2EDE6' },
  loader: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },

  card: { flex: 1, backgroundColor: '#17201B', borderRadius: 16, padding: 16, marginBottom: 12, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.07, shadowRadius: 6, elevation: 3, borderTopWidth: 3, borderTopColor: '#21808D' },
  cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 10 },
  avatar: { width: 50, height: 50, borderRadius: 25, justifyContent: 'center', alignItems: 'center' },
  avatarImg: { width: 50, height: 50, borderRadius: 25, backgroundColor: '#f1f5f9' },
  avatarTxt: { color: '#fff', fontSize: 18, fontWeight: '700' },
  name: { fontSize: 15, fontWeight: '600', color: '#E2EDE6' },
  email: { fontSize: 12, color: '#21808D', marginTop: 2 },
  phone: { fontSize: 12, color: '#7A9A80', marginTop: 1 },
  badge: { borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4 },
  badgeTxt: { fontSize: 11, fontWeight: '600' },
  infoGrid: { gap: 6, marginBottom: 12 },
  infoRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  infoIcon: { fontSize: 13, width: 18 },
  infoTxt: { fontSize: 13, color: '#7A9A80', flex: 1 },
  actions: { flexDirection: 'row', gap: 10 },
  editBtn: { flex: 1, backgroundColor: '#e8f5f7', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  editTxt: { color: '#21808D', fontWeight: '600', fontSize: 13 },
  delBtn: { flex: 1, backgroundColor: '#fee2e2', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  delTxt: { color: '#ef4444', fontWeight: '600', fontSize: 13 },
  empty: { alignItems: 'center', paddingTop: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: '#E2EDE6', marginTop: 12 },
});

// ── Wizard styles ─────────────────────────────────────────────────
const wi = StyleSheet.create({
  screen: { flex: 1, backgroundColor: '#17201B' },
  topBar: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, backgroundColor: '#17201B', borderBottomWidth: 1, borderBottomColor: '#e2e8f0', gap: 12 },
  closeBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center' },
  closeTxt: { fontSize: 14, color: '#7A9A80', fontWeight: '600' },
  topTitle: { flex: 1, fontSize: 18, fontWeight: '700', color: '#E2EDE6', textAlign: 'center' },

  progressArea: { backgroundColor: '#141E18', paddingHorizontal: 20, paddingTop: 14, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  progressBarOuter: { height: 4, backgroundColor: '#e2e8f0', borderRadius: 2, marginBottom: 10 },
  progressBarInner: { height: 4, backgroundColor: '#21808D', borderRadius: 2 },
  progressRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 },
  progressLabel: { fontSize: 15, fontWeight: '700', color: '#E2EDE6' },
  progressCount: { fontSize: 12, color: '#7A9A80', fontWeight: '600' },
  progressSub: { fontSize: 12, color: '#7A9A80', marginBottom: 10 },
  dots: { flexDirection: 'row', gap: 6 },
  dot: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#e2e8f0', justifyContent: 'center', alignItems: 'center' },
  dotActive: { backgroundColor: '#21808D' },
  dotDone: { backgroundColor: '#10b981' },
  dotTxt: { fontSize: 11, fontWeight: '700', color: '#94a3b8' },
  dotTxtDone: { fontSize: 11, fontWeight: '700', color: '#fff' },

  scrollContent: { padding: 20, paddingBottom: 24 },

  footer: { flexDirection: 'row', gap: 12, paddingHorizontal: 20, paddingVertical: 14, borderTopWidth: 1, borderTopColor: '#e2e8f0', backgroundColor: '#17201B' },
  backBtn: { flex: 1, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  backBtnTxt: { color: '#7A9A80', fontWeight: '600', fontSize: 15 },
  nextBtn: { flex: 2, backgroundColor: '#21808D', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  nextBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },
  saveBtn: { flex: 2, backgroundColor: '#10b981', borderRadius: 12, paddingVertical: 14, alignItems: 'center' },
  saveBtnTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },

  fg: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#A8C4AF', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: '#E2EDE6', backgroundColor: '#fafafa' },
  row: { flexDirection: 'row', gap: 12 },

  infoBox: { backgroundColor: '#e8f5f7', borderRadius: 10, padding: 12, marginBottom: 14 },
  infoTxt: { fontSize: 13, color: '#21808D', fontWeight: '500' },

  sectionLabel: { fontSize: 12, fontWeight: '700', color: '#21808D', letterSpacing: 0.5, marginBottom: 10, textTransform: 'uppercase' as const },

  photoSection: { flexDirection: 'row', gap: 20, alignItems: 'center', marginBottom: 8 },
  photoWrap: { width: 100, height: 100, borderRadius: 50, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center', borderWidth: 2, borderColor: '#21808D', borderStyle: 'dashed' as const, overflow: 'hidden' },
  photoImg: { width: 100, height: 100, borderRadius: 50 },
  photoPlaceholder: { alignItems: 'center', gap: 4 },
  photoHint: { fontSize: 10, color: '#21808D', fontWeight: '600' },
  uploadBtn: { backgroundColor: '#21808D', paddingHorizontal: 18, paddingVertical: 10, borderRadius: 10, alignItems: 'center' },
  uploadBtnTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
  removeBtn: { backgroundColor: '#fee2e2', paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10, alignItems: 'center' },
  removeBtnTxt: { color: '#ef4444', fontWeight: '600', fontSize: 12 },

  switchRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 12, paddingHorizontal: 14, backgroundColor: '#fafafa' },
  switchLabel: { fontSize: 14, fontWeight: '600', color: '#E2EDE6' },
  switchSub: { fontSize: 12, color: '#7A9A80', marginTop: 2 },
});
